import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Truck } from 'lucide-react';
import { formatDate, formatMoney, formatQty, PERMISSIONS } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { InlineView } from '@/components/ui/inline-view';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface SuggestedLine {
  productId: string; sku: string; name: string;
  /** En unidad de compra (rollos, cajas). */
  quantity: number;
  /** Lo que falta de verdad, en unidad base. */
  deficitBase: number;
  alreadyOrderedBase: number;
  unit: string | null; unitCost: number;
  requiredBy: string | null; recommendedOrderDate: string | null;
}

interface SuggestedGroup {
  supplierId: string; supplierName: string; lines: SuggestedLine[];
  suggestedDate: string | null; orderByDate: string | null; estimatedTotal: number;
}

interface Suggestions {
  run: { id: string; createdAt: string } | null;
  suppliers: SuggestedGroup[];
  unassigned: SuggestedLine[];
}

/**
 * Pedidos sugeridos a partir del último cálculo del MRP. El déficit se agrupa por proveedor
 * porque así se compra: un pedido por proveedor, con las cantidades que faltan y el último
 * coste conocido. Nada se pide sin que alguien lo revise y pulse el botón.
 */
export function SuggestionsPanel({ open, onOpenChange, onCreated }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const puedePedir = can(PERMISSIONS.PURCHASES_WRITE);

  const suggestionsQuery = useQuery({
    queryKey: ['purchase-suggestions'],
    queryFn: () => api<Suggestions>('/purchase-orders/suggestions'),
    enabled: open
  });
  const datos = suggestionsQuery.data;

  // Lo editado se guarda **por producto**, no por posición: si la planificación cambia entre la
  // edición y la creación, un índice aplicaría el precio de un artículo a otro.
  const [borrador, setBorrador] = useState<Record<string, Record<string, { quantity?: string; unitCost?: string }>>>({});
  const [creando, setCreando] = useState<string | null>(null);
  const [runVisto, setRunVisto] = useState<string | null>(null);

  // Un run nuevo invalida lo editado: los productos y las cantidades ya no son los mismos.
  // La propuesta se recalcula en vivo, así que lo editado se reinicia cuando cambia la lista (no
  // solo cuando cambia la planificación): si no, se aplicarían cantidades viejas a líneas nuevas.
  const firma = datos ? `${datos.run?.id ?? ''}:${datos.suppliers.map((g) => g.lines.map((l) => `${l.productId}=${l.quantity}`).join(',')).join('|')}` : null;
  if (firma && firma !== runVisto) {
    setRunVisto(firma);
    setBorrador({});
  }

  const valorDe = (group: SuggestedGroup, line: SuggestedLine) => {
    const editado = borrador[group.supplierId]?.[line.productId];
    return {
      quantity: editado?.quantity ?? String(line.quantity),
      unitCost: editado?.unitCost ?? String(line.unitCost)
    };
  };

  const ajustar = (group: SuggestedGroup, line: SuggestedLine, campo: 'quantity' | 'unitCost', valor: string) => {
    setBorrador((previo) => ({
      ...previo,
      [group.supplierId]: {
        ...(previo[group.supplierId] ?? {}),
        [line.productId]: { ...(previo[group.supplierId]?.[line.productId] ?? {}), [campo]: valor }
      }
    }));
  };

  const totalGrupo = (group: SuggestedGroup) => group.lines.reduce((acc, line) => {
    const { quantity, unitCost } = valorDe(group, line);
    const cantidad = Number(quantity);
    const coste = Number(unitCost);
    if (!Number.isFinite(cantidad) || cantidad <= 0) return acc;
    return acc + cantidad * (Number.isFinite(coste) ? coste : 0);
  }, 0);

  const crearPedido = async (group: SuggestedGroup) => {
    const lineas = group.lines
      .map((line) => ({ line, valores: valorDe(group, line) }))
      .filter(({ valores }) => Number(valores.quantity) > 0)
      .map(({ line, valores }) => ({
        productId: line.productId,
        quantity: Number(valores.quantity),
        unitCost: Number(valores.unitCost) || 0
      }));
    if (!lineas.length) { toast.error('No hay cantidades que pedir en este proveedor'); return; }
    setCreando(group.supplierId);
    try {
      await api('/purchase-orders', {
        method: 'POST',
        body: {
          supplierId: group.supplierId,
          // Fecha prevista de **llegada**, que es lo que significa expectedDate en el pedido.
          expectedDate: group.suggestedDate,
          notes: 'Sugerido por el MRP',
          lines: lineas
        }
      });
      toast.success(`Pedido creado a ${group.supplierName}`);
      setBorrador((previo) => {
        const copia = { ...previo };
        delete copia[group.supplierId];
        return copia;
      });
      await suggestionsQuery.refetch();
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      onCreated();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCreando(null);
    }
  };

  const sinProveedor = useMemo(() => datos?.unassigned ?? [], [datos]);
  const grupos = datos?.suppliers ?? [];

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="xl"
      title="Pedidos sugeridos del MRP"
      description="Lo que falta ahora mismo según la planificación, agrupado por proveedor. Revisa cantidades y costes antes de crear el pedido."
      footer={<Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>}
    >
      <div className="space-y-4">
        {suggestionsQuery.isLoading ? <p className="text-xs text-muted-foreground">Cargando la planificación…</p> : null}
        {!suggestionsQuery.isLoading && !datos?.run ? (
          <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
            Todavía no hay ninguna planificación calculada. Lanza el MRP en Planificación y vuelve aquí.
          </p>
        ) : null}
        {datos?.run ? (
          <p className="text-[11px] text-muted-foreground">
            Planificación del {formatDate(datos.run.createdAt)} · {grupos.length} proveedor{grupos.length === 1 ? '' : 'es'} con material pendiente
          </p>
        ) : null}

        {grupos.map((group) => {
          return (
            <div key={group.supplierId} className="rounded-lg border border-border">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
                <div>
                  <p className="flex items-center gap-2 text-sm font-medium"><Truck className="size-4 text-muted-foreground" /> {group.supplierName}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {group.lines.length} artículo{group.lines.length === 1 ? '' : 's'}
                    {group.suggestedDate ? ` · recibir antes del ${formatDate(group.suggestedDate)}` : ''}
                    {group.orderByDate ? ` · pedir el ${formatDate(group.orderByDate)}` : ''}
                    {' · '}estimado {formatMoney(totalGrupo(group))}
                  </p>
                </div>
                {puedePedir ? (
                  <Button size="sm" loading={creando === group.supplierId} onClick={() => void crearPedido(group)}>
                    <Sparkles /> Crear pedido
                  </Button>
                ) : null}
              </div>
              <div className="divide-y divide-border">
                {group.lines.map((line) => {
                  const valores = valorDe(group, line);
                  return (
                  <div key={line.productId} className="grid gap-2 px-3 py-2 sm:grid-cols-[1fr_100px_110px_140px]">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium">{line.name}</p>
                      <p className="font-mono text-[10px] text-muted-foreground">{line.sku}</p>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Cantidad</label>
                      <Input type="number" min={0} step="1" value={valores.quantity}
                        disabled={!puedePedir}
                        onChange={(e) => ajustar(group, line, 'quantity', e.target.value)} />
                      <span className="text-[10px] text-muted-foreground">
                        {line.unit ?? 'ud'} · faltan {formatQty(line.deficitBase)}
                        {line.alreadyOrderedBase > 0 ? `, ${formatQty(line.alreadyOrderedBase)} ya pedidos` : ''}
                      </span>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Coste</label>
                      <Input type="number" min={0} step="0.01" value={valores.unitCost}
                        disabled={!puedePedir}
                        onChange={(e) => ajustar(group, line, 'unitCost', e.target.value)} />
                      <span className="text-[10px] text-muted-foreground">último conocido</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {line.requiredBy ? <p>Se necesita el {formatDate(line.requiredBy)}</p> : null}
                      {line.recommendedOrderDate ? <p>Pedir el {formatDate(line.recommendedOrderDate)}</p> : null}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {sinProveedor.length ? (
          <div className="rounded-lg border border-warning/60 bg-warning/10 px-3 py-2">
            <p className="text-xs font-medium">Material sin proveedor asignado: no se puede pedir desde aquí.</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {sinProveedor.map((line) => `${line.sku} (${formatQty(line.quantity)} ${line.unit ?? 'ud'})`).join(' · ')}
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">Asigna un proveedor al artículo en su ficha y vuelve a abrir esta ventana.</p>
          </div>
        ) : null}

        {datos?.run && !grupos.length && !sinProveedor.length ? (
          <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
            La planificación no tiene déficit: no hay nada que pedir.
          </p>
        ) : null}
      </div>
    </InlineView>
  );
}
